import { useCallback, useState } from 'react'

import { useShipmentToolbarCounts } from '#/entities/shipment'
import { useCreateToolbarItem } from '#/features/shipment-toolbar'

import { SHIPMENT_COMMENT_TAB } from './comment-panel-tabs'
import type { ShipmentCommentTab } from './comment-panel-tabs'
import { useContractSlice } from './comment-panel/contract-slice'
import { useEmailSlice } from './comment-panel/email-slice'
import { useFileSlice } from './comment-panel/file-slice'
import { useNoteSlice } from './comment-panel/note-slice'
import { usePaymentSlice } from './comment-panel/payment-slice'
import { usePhoneSlice } from './comment-panel/phone-slice'
import { useTabs } from './comment-panel/tabs'
import { useTaskSlice } from './comment-panel/task-slice'

interface UseShipmentCommentPanelOptions {
  hiddenTabs?: ShipmentCommentTab[]
}

interface ArmedFlags {
  contractArmed: boolean
  phoneArmed: boolean
  emailArmed: boolean
  paymentArmed: boolean
}

function useShipmentCommentSlices(
  shipmentId: string | undefined,
  closeTab: () => void,
  flags: ArmedFlags,
) {
  const {
    createNote,
    isNotePending,
    createTask,
    isTaskPending,
    createContract,
    isContractPending,
    createSms,
    isSmsPending,
    uploadFile,
    isFileUploading,
    createEmail,
    isEmailPending,
    createPayment,
    isPaymentPending,
  } = useCreateToolbarItem(shipmentId)

  const note = useNoteSlice({
    shipmentId,
    createNote,
    isPending: isNotePending,
    closeTab,
  })
  const task = useTaskSlice({
    shipmentId,
    createTask,
    isPending: isTaskPending,
    closeTab,
  })
  const phone = usePhoneSlice({
    shipmentId,
    armed: flags.phoneArmed,
    createSms,
    isSending: isSmsPending,
    closeTab,
  })
  const email = useEmailSlice({
    shipmentId,
    armed: flags.emailArmed,
    createEmail,
    uploadFile,
    isSending: isEmailPending,
    isUploading: isFileUploading,
    closeTab,
  })
  const file = useFileSlice({
    shipmentId,
    uploadFile,
    isUploading: isFileUploading,
    closeTab,
  })
  const contract = useContractSlice({
    shipmentId,
    armed: flags.contractArmed,
    createContract,
    isPending: isContractPending,
  })
  const payment = usePaymentSlice({
    shipmentId,
    armed: flags.paymentArmed,
    createPayment,
    isPending: isPaymentPending,
  })

  return { note, task, phone, email, file, contract, payment }
}

export function useShipmentCommentPanel(
  shipmentId: string | undefined,
  { hiddenTabs }: UseShipmentCommentPanelOptions = {},
) {
  const { counts } = useShipmentToolbarCounts(shipmentId)
  const allTabs = useTabs({
    note: (counts?.note ?? 0) > 0,
    task: (counts?.task ?? 0) > 0,
    file: (counts?.file ?? 0) > 0,
  })
  const tabs = hiddenTabs?.length
    ? allTabs.filter((t) => !hiddenTabs.includes(t.value as ShipmentCommentTab))
    : allTabs

  const [tab, setTabRaw] = useState<ShipmentCommentTab | null>(null)
  const [contractArmed, setContractArmed] = useState(false)
  const [phoneArmed, setPhoneArmed] = useState(false)
  const [emailArmed, setEmailArmed] = useState(false)
  const [paymentArmed, setPaymentArmed] = useState(false)

  const setTab = useCallback((next: ShipmentCommentTab | null) => {
    if (next === SHIPMENT_COMMENT_TAB.contract) setContractArmed(true)
    if (next === SHIPMENT_COMMENT_TAB.phone) setPhoneArmed(true)
    if (next === SHIPMENT_COMMENT_TAB.email) setEmailArmed(true)
    if (next === SHIPMENT_COMMENT_TAB.payment) setPaymentArmed(true)
    setTabRaw(next)
  }, [])

  const closeTab = useCallback(() => setTab(null), [setTab])

  const slices = useShipmentCommentSlices(shipmentId, closeTab, {
    contractArmed,
    phoneArmed,
    emailArmed,
    paymentArmed,
  })

  return {
    tabs,
    tab,
    setTab,
    ...slices,
  }
}
